import { useState, useCallback } from 'react';
import { z } from 'zod';
import type { ProductFormData, UploadState } from '@/types';
import { api } from '@/lib/api';
import { uploadToR2 } from '@/lib/r2-uploader';
import { validateMedia, getImageDimensions, getVideoMetadata } from '@/lib/media-validator';

const productSchema = z.object({
  name: z.string().min(1, 'Product name is required'),
  price: z.number().positive('Price must be positive'),
  cost: z.number().positive('Cost must be positive'),
  category: z.string().min(1, 'Category is required'),
  sku: z.string().min(1, 'SKU is required'),
  stock: z.number().int().min(0, 'Stock cannot be negative'),
  description: z.string().min(1, 'Description is required'),
  seoTitle: z.string().min(1, 'SEO title is required'),
  seoDescription: z.string().min(1, 'SEO description is required'),
});

interface UseUploaderReturn {
  formData: ProductFormData;
  updateField: <K extends keyof ProductFormData>(key: K, value: ProductFormData[K]) => void;
  errors: Record<string, string>;
  uploadStates: UploadState[];
  videoUploadState: UploadState | null;
  loading: boolean;
  validate: () => boolean;
  submit: () => Promise<string | null>;
  reset: () => void;
}

const defaultFormData: ProductFormData = {
  name: '',
  slug: '',
  description: '',
  price: 0,
  cost: 0,
  category: '',
  brand: '',
  sku: '',
  stock: 0,
  status: 'draft',
  images: [],
  specs: {},
  variations: [],
  seoTitle: '',
  seoDescription: '',
  seoKeywords: [],
  tags: [],
};

export function useUploader(initialData?: Partial<ProductFormData>): UseUploaderReturn {
  const [formData, setFormData] = useState<ProductFormData>({
    ...defaultFormData,
    ...initialData,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploadStates, setUploadStates] = useState<UploadState[]>([]);
  const [videoUploadState, setVideoUploadState] = useState<UploadState | null>(null);
  const [loading, setLoading] = useState(false);

  const updateField = useCallback(
    <K extends keyof ProductFormData>(key: K, value: ProductFormData[K]) => {
      setFormData((prev) => {
        const updated = { ...prev, [key]: value };
        if (key === 'name' && !initialData?.id) {
          updated.slug = value
            .toString()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '');
        }
        return updated;
      });
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    [initialData?.id],
  );

  const validate = useCallback((): boolean => {
    const result = productSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      for (const error of result.error.errors) {
        fieldErrors[error.path[0]] = error.message;
      }
      setErrors(fieldErrors);
      return false;
    }
    setErrors({});
    return true;
  }, [formData]);

  const uploadFile = useCallback(
    async (file: File): Promise<{ key: string; url: string } | null> => {
      const validation = validateMedia(file);
      if (!validation.valid) {
        throw new Error(validation.errors.join(', '));
      }

      const presigned = await api.media.getPresignedUrl(file.name, file.type);

      return new Promise((resolve) => {
        const stateIndex = uploadStates.length;
        setUploadStates((prev) => [
          ...prev,
          { status: 'uploading', progress: 0 },
        ]);

        uploadToR2({
          file,
          uploadUrl: presigned.uploadUrl,
          key: presigned.key,
          onProgress: (progress) => {
            setUploadStates((prev) => {
              const next = [...prev];
              next[stateIndex] = { ...next[stateIndex], progress };
              return next;
            });
          },
        }).then((result) => {
          if (result.success) {
            setUploadStates((prev) => {
              const next = [...prev];
              next[stateIndex] = {
                status: 'success',
                progress: 100,
                key: result.key,
                url: presigned.publicUrl,
              };
              return next;
            });
            resolve({ key: result.key, url: presigned.publicUrl });
          } else {
            setUploadStates((prev) => {
              const next = [...prev];
              next[stateIndex] = {
                status: 'error',
                progress: 0,
                error: result.error,
              };
              return next;
            });
            resolve(null);
          }
        });
      });
    },
    [uploadStates.length],
  );

  const handleImageUpload = useCallback(
    async (files: File[]) => {
      setLoading(true);
      const uploadedImages = [...formData.images];

      for (const file of files) {
        if (!file.type.startsWith('image/')) continue;

        const result = await uploadFile(file);
        if (result) {
          const dims = await getImageDimensions(file);
          uploadedImages.push({
            id: crypto.randomUUID(),
            key: result.key,
            url: result.url,
            type: 'image',
            mimeType: file.type,
            size: file.size,
            width: dims.width,
            height: dims.height,
            uploadedAt: new Date().toISOString(),
          });
        }
      }

      updateField('images', uploadedImages);
      setLoading(false);
    },
    [formData.images, uploadFile, updateField],
  );

  const handleVideoUpload = useCallback(
    async (file: File) => {
      if (!file.type.startsWith('video/')) return;

      setLoading(true);
      setVideoUploadState({ status: 'uploading', progress: 0 });

      const presigned = await api.media.getPresignedUrl(file.name, file.type);

      const result = await uploadToR2({
        file,
        uploadUrl: presigned.uploadUrl,
        key: presigned.key,
        onProgress: (progress) => {
          setVideoUploadState((prev) => (prev ? { ...prev, progress } : null));
        },
      });

      if (result.success) {
        const metadata = await getVideoMetadata(file);
        updateField('video', {
          id: crypto.randomUUID(),
          key: result.key,
          url: presigned.publicUrl,
          type: 'video',
          mimeType: file.type,
          size: file.size,
          width: metadata.width,
          height: metadata.height,
          duration: metadata.duration,
          uploadedAt: new Date().toISOString(),
        });
        setVideoUploadState({ status: 'success', progress: 100 });
      } else {
        setVideoUploadState({ status: 'error', progress: 0, error: result.error });
      }

      setLoading(false);
    },
    [updateField],
  );

  const submit = useCallback(async (): Promise<string | null> => {
    if (!validate()) return null;

    setLoading(true);
    try {
      const data = { ...formData, images: formData.images, video: formData.video };

      if (formData.id) {
        const result = await api.products.update(formData.id, data);
        return result.id || null;
      } else {
        const result = await api.products.create(data);
        return result.id || null;
      }
    } catch (error) {
      setErrors({ submit: error instanceof Error ? error.message : 'Failed to save product' });
      return null;
    } finally {
      setLoading(false);
    }
  }, [formData, validate]);

  const reset = useCallback(() => {
    setFormData({ ...defaultFormData });
    setErrors({});
    setUploadStates([]);
    setVideoUploadState(null);
    setLoading(false);
  }, []);

  return {
    formData,
    updateField,
    errors,
    uploadStates,
    videoUploadState,
    loading,
    validate,
    submit,
    reset,
  };
}
